import { useContext } from "react";

import { type UiIconNames } from "@icg360/design-system";
import { Button, Card, Heading, Icon, Span } from "@icg360/design-system";

import {
  coverages,
  newCoveragePremiums,
  newExtendedCoveragesCopy,
} from "consts";
import { trackEvent } from "utils";

import { CoverageContext } from "../hooks";
import styles from "./coverage-card.module.scss";

const coverageIcons: Record<string, UiIconNames> = {
  [coverages.IDENTITY]: "IdentityProtection",
  [coverages.MECHANICAL]: "Tools",
  [coverages.SERVICE]: "Volt",
};

type CoverageName = "IDENTITY_FRAUD" | "MECHANICAL_BREAKDOWN" | "SERVICE_LINE";

type CoverageCardProps = {
  coverage: {
    name: CoverageName;
    premium?: string | null | undefined;
    active?: boolean;
  } | null;
  listOfCoverages: ({
    name: string;
  } | null)[];
  isLastItem?: boolean;
};

export const CoverageCard = ({
  coverage,
  listOfCoverages,
  isLastItem,
}: CoverageCardProps) => {
  const { setCoveragePage } = useContext(CoverageContext);

  if (!coverage) {
    return null;
  }

  const setCoverage = () => {
    trackEvent("Coverage Button Clicked", {
      coverageName: coverage.name,
      coveragePrice: newCoveragePremiums[coverage.name],
      listOfCoverages: listOfCoverages?.map((c) => c?.name) ?? [],
    });

    setCoveragePage({
      policyId: "",
      type: "open",
      y: window.pageYOffset,
    });
  };

  const CoverageCardLayout = () => (
    <Card
      border={false}
      style={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
      }}
    >
      <div className={styles.coverageItemHeader} key={coverage.name}>
        <div className={styles.coverageIcon}>
          <Icon name={coverageIcons[coverage.name]} size="lg" />
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            <Heading size="xs">
              {newExtendedCoveragesCopy[coverage.name].short}
            </Heading>
            <Span color="quiet">
              {newExtendedCoveragesCopy[coverage.name].description.short}
            </Span>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <>
      <CoverageCardLayout />
      {isLastItem && (
        <div className={styles.buttonContainer}>
          <Button
            trailingIcon="ArrowRight"
            appearance="secondary"
            size="default"
            onPress={setCoverage}
            data-testid="extended-coverage-check-price-btn"
          >
            Check pro-rated prices
          </Button>
        </div>
      )}
    </>
  );
};
